
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/contexts/auth";
import { ProfileFormValues } from "./types";
import { createProfileFormSchema } from "./useProfileSchema";
import { createFallbackValidator } from "./useProfileFormUtils";
import { useProfileFormSubmit } from "./useProfileFormSubmit";
import * as z from "zod";

// Create a schema without relying on direct zod exports
export const profileFormSchema = createProfileFormSchema();

export const useProfileForm = () => {
  const { user, profile, updateUserProfile, refreshProfile } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  
  // Create form with more error handling around zodResolver
  const form = useForm<ProfileFormValues>({
    // Use a safer approach to avoid zodResolver crashes
    resolver: (() => {
      try {
        // Try the standard zodResolver
        return zodResolver(profileFormSchema as z.ZodType<any>);
      } catch (error) {
        console.error("Falling back to manual validation:", error);
        // Fallback to a simple validation function if zodResolver fails
        return createFallbackValidator();
      }
    })(),
    defaultValues: {
      display_username: profile?.display_username || "",
      university_id: profile?.university_id || "",
      university: profile?.university || "",
      lab_id: profile?.lab_id || "",
      lab: profile?.lab || "",
      city: profile?.city || "",
      country: profile?.country || "",
      research_interests: profile?.research_interests || "",
      expertise: profile?.expertise || "",
      linkedin_url: profile?.linkedin_url || "",
      website_url: profile?.website_url || "",
      email: user?.email || "",
      newPassword: "",
      confirmPassword: "",
      email_notifications: profile?.email_notifications ?? true,
      notify_swap_offers: profile?.notify_swap_offers ?? true,
      notify_swap_responses: profile?.notify_swap_responses ?? true,
      daily_digest_email: profile?.daily_digest_email ?? true,
      notify_messages: profile?.notify_messages ?? true,
      weekly_digest_email: profile?.weekly_digest_email ?? true,
      university_only_swaps: (profile as any)?.university_only_swaps ?? false,
    }
  });

  // Update form values when profile loads or changes
  useEffect(() => {
    if (profile && user) {
      form.reset({
        display_username: profile.display_username || "",
        university_id: profile.university_id || "",
        university: profile.university || "",
        lab_id: profile.lab_id || "",
        lab: profile.lab || "",
        city: profile.city || "",
        country: profile.country || "",
        research_interests: profile.research_interests || "",
        expertise: profile.expertise || "",
        linkedin_url: profile.linkedin_url || "",
        website_url: profile.website_url || "",
        email: user.email || "",
        newPassword: "",
        confirmPassword: "",
        email_notifications: profile.email_notifications ?? true,
        notify_swap_offers: profile.notify_swap_offers ?? true,
        notify_swap_responses: profile.notify_swap_responses ?? true,
        daily_digest_email: profile.daily_digest_email ?? true,
        notify_messages: profile.notify_messages ?? true,
        weekly_digest_email: profile.weekly_digest_email ?? true,
        university_only_swaps: (profile as any)?.university_only_swaps ?? false,
      });
    }
  }, [profile, user, form]);

  // Use profile form submit hook with loading state wrapper
  const handleFormSubmit = useProfileFormSubmit(form, updateUserProfile, refreshProfile, user);
  
  const onSubmit = async (data: ProfileFormValues) => {
    setIsLoading(true);
    await handleFormSubmit(data);
    setIsLoading(false);
  };

  return {
    form,
    isLoading,
    onSubmit,
    watch: form.watch
  };
};
